import _ from 'lodash';
import { sqDurationStore, sqTrendConditionStore, sqTrendSeriesStore, sqTrendStore } from '@/core/core.stores';
import { flux } from '@/core/flux.module';
import { PUSH_IGNORE } from '@/core/flux.service';
import { fetchMonitoredConditionIds } from '@/notifications/notifications.actions';
import { CANCELLATION_GROUP_GUID_SEPARATOR, cancelGroup } from '@/requests/pendingRequests.utilities';
import { fetchTreemap } from '@/treemap/treemap.actions';
import { getAlignableItems, getAllItems, getItemId } from '@/trend/trendDataHelper.utilities';
import { calculate } from '@/trend/trendViewer/capsuleBuckets.utilities';
import { recordDataStatus } from '@/trendData/itemRuntime.utilities';
import {
  fetchChartCapsules,
  fetchItemProps,
  fetchStatistics,
  fetchTableAndChartCapsules,
  setCustomizationProps,
  setTrendItemProps,
} from '@/trendData/trend.actions';
import { reindexOrderInLaneForLane } from '@/trendData/trendConditionLayout.utilities';
import { updateCustomLabelTargets } from '@/trendData/trendCustomization.actions';
import { ITEM_DATA_STATUS, ITEM_TYPES } from '@/trendData/trendData.constants';
import { getLaneForReservation, reindexOrderInLaneForReservation } from '@/trendData/trendLaneReservation.utilities';
import { addConditionGroup } from '@/utilities/autoGroup.utilities';
import { isCanceled } from '@/utilities/http.utilities';
import { logError } from '@/utilities/logger';
import { formatMessage } from '@/utilities/logger.utilities';
import type { PushOption } from '@/core/flux.service';
import type { DataStatusPayload } from '@/trendData/itemStoreBase.types';
import type { CustomLabelTargetUpdate, LaneCustomLabel } from '@/trendData/trendData.constants';
import type { BaseItem, ConditionItem } from '@/trendData/trendData.types';

export function setConditionDataStatusLoading(id: string): void {
  recordDataStatus(ITEM_DATA_STATUS.LOADING, { id });
}

export function setConditionDataStatusPresent(payload: DataStatusPayload): void {
  recordDataStatus(ITEM_DATA_STATUS.PRESENT, payload);
}

export function setConditionDataStatusHiddenFromTrend(id: string, ...option: [PushOption?]): void {
  recordDataStatus(ITEM_DATA_STATUS.HIDDEN_FROM_TREND, { id });
  flux.dispatch('TREND_SET_DATA_STATUS_HIDDEN_FROM_TREND', { id }, ...option);
}

export function setEditModeForCondition(conditionId: string | number | null) {
  const oldEditingId = sqTrendSeriesStore.editingId;
  flux.dispatch('TREND_SET_EDITING_CAPSULE_SET_ID', { id: conditionId });
  if (conditionId !== oldEditingId) {
    fetchTableAndChartCapsules();
  }
}

export function addCondition(item: ConditionItem, props?: object, option?: PushOption) {
  if (!sqTrendConditionStore.findItem(item.id)) {
    const reservation = item.laneReservation
      ? getLaneForReservation(
          item.laneReservation,
          item.laneReservationOptions,
          setCustomizationProps,
          () => getAlignableItems({}) as BaseItem[],
        )
      : undefined;
    const explicitLane = (props as Record<string, unknown> | undefined)?.lane;
    const payload = {
      id: item.id,
      name: item.name,
      isArchived: !!item.isArchived,
      color: item.color,
      effectivePermissions: item.effectivePermissions,
      lane: item.lane ?? sqTrendStore.getNextLane(false),
      lineWidth: 1,
      ...(item.overlay !== undefined ? { overlay: item.overlay } : {}),
      laneReservation: item.laneReservation,
      orderInLane: item.orderInLane,
      orderInLanePriority: item.orderInLanePriority,
    };

    if (reservation) {
      payload.lane = reservation.lane;
    } else if (explicitLane !== undefined) {
      payload.lane = explicitLane as number;
    } else if (item.laneReservation) {
      payload.lane = sqTrendStore.getNextLane(false) as number;
    } else {
      // Default behavior: insert above the first series/scalar lane.
      const items = getAllItems({});
      const conditionLane = _.chain(items)
        .filter((item) => item.itemType === ITEM_TYPES.SERIES || item.itemType === ITEM_TYPES.SCALAR)
        .map('lane')
        .min()
        .value();
      if (conditionLane) {
        payload.lane = conditionLane;
        const newLane = conditionLane;

        const itemCustomizations = _.chain(items)
          .filter((item) => item.lane >= newLane)
          .map((item) => ({ id: item.id, lane: item.lane + 1 }))
          .value();
        setCustomizationProps(itemCustomizations);

        // Move the target for custom lane labels below newly added condition downward by 1 lane
        const customLaneLabelTargetUpdates: CustomLabelTargetUpdate[] =
          sqTrendStore.labelDisplayConfiguration.customLabels
            .filter((label): label is LaneCustomLabel => label.location === 'lane' && label.target >= newLane)
            .map((label) => ({ currentTarget: label.target, newTarget: label.target + 1 }));
        updateCustomLabelTargets(customLaneLabelTargetUpdates, PUSH_IGNORE);
      }
    }

    flux.dispatch('TREND_ADD_CAPSULE_SET', payload, option);

    if (item.laneReservation) {
      reindexOrderInLaneForReservation(
        item.laneReservation,
        setCustomizationProps,
        () => getAlignableItems({}) as BaseItem[],
      );
    } else if (item.orderInLane !== undefined || item.orderInLanePriority !== undefined) {
      reindexOrderInLaneForLane(payload.lane, setCustomizationProps, () => getAlignableItems({}) as BaseItem[]);
    }
  }

  setTrendItemProps(item.id, props, option);

  // Fetch chart and details first to get it on screen quickly and to ensure formula warnings show (CRAB-11702)
  return Promise.all([
    fetchItemProps(item.id).then(() => {
      addConditionGroup({
        id: item.id,
      });
    }),
    fetchChartCapsules([item]),
  ]).then(() =>
    Promise.all([
      fetchTreemap(),
      fetchTimebarCapsules(item.id),
      fetchTableAndChartCapsules(),
      fetchStatistics(sqTrendConditionStore.findItem(item.id)),
      fetchMonitoredConditionIds([item.id]),
    ]),
  );
}

/**
 * Fetches the capsules for all timebar conditions.
 */
export function fetchAllTimebarCapsules() {
  return Promise.all(
    _.map(
      getAllItems({
        excludeEditingCondition: true,
        workingSelection: true,
        excludeDataStatus: [ITEM_DATA_STATUS.REDACTED],
        itemTypes: [ITEM_TYPES.CONDITION],
        excludeDummyItems: true,
      }),
      (item) => {
        const id = getItemId(item);
        return fetchTimebarCapsules(id).then((regions) => ({ id, regions }));
      },
    ),
  );
}

/**
 * Fetches the capsules, in an aggregated form, for the timebar and dispatches the results.
 *
 * @param {String} id - The id of the condition for which to fetch capsules
 * @returns {Promise} A promise that resolves when the results have been fetched.
 */
export function fetchTimebarCapsules(id: string) {
  if (!sqTrendStore.capsulePreview) {
    return Promise.resolve([]);
  }
  const cancellationGroup = `timebarCapsules${CANCELLATION_GROUP_GUID_SEPARATOR}${id}`;
  const item = sqTrendConditionStore.findItem(id);
  const range = sqDurationStore.investigateRange;
  const width = document.querySelector('#timebar')?.clientWidth;

  if (_.isNil(width) || width <= 0) {
    return Promise.resolve([]);
  }

  const buckets = Math.floor(width / 2);
  const bucketWidthArg = `${range.duration.asMilliseconds() / buckets}ms`;
  return cancelGroup(cancellationGroup)
    .then(() =>
      calculate({
        item,
        range,
        bucketWidthArg,
        cancellationGroup,
      }),
    )
    .catch((e) => {
      if (!isCanceled(e)) {
        logError(formatMessage`Error with fetchTimebarCapsules: ${e}`);
      }
      return [];
    });
}
